import {
  Button,
  DataTable,
  Loading,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from "carbon-components-react";
import { DataView24 } from "@carbon/icons-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import Card from "../../components/Card/Card";
import TablePagination from "../../components/Pagination/Pagination";
import http from "../../http-common";

/**
 * @typedef {Object} BusinessPlanRankingItem
 * @property {number} position
 * @property {number} businessPlanId
 * @property {string} uuid
 * @property {string} nur
 * @property {string} competitorName
 * @property {string} windowDesc
 * @property {number} totalEvaluations
 * @property {number | null} totalScoreJury1
 * @property {number | null} totalScoreJury2
 * @property {number | null} totalScoreJury3
 * @property {number | null} averageScore
 * @property {number | null} averagePercentage
 */

const tableHeaders = [
  { header: "Posição", key: "position" },
  // { header: "ID Plano", key: "businessPlanId" },
  // { header: "UUID", key: "uuid" },
  { header: "NUR", key: "nur" },
  { header: "Janela", key: "window" },
  // { header: "Concorrente", key: "competitorName" },
  { header: "# de Avaliações", key: "totalEvaluations" },
  { header: "Júri 1", key: "totalScoreJury1" },
  { header: "Júri 2", key: "totalScoreJury2" },
  { header: "Diferença (%)", key: "differencePercentage" },
  { header: "Júri 3", key: "totalScoreJury3" },

  { header: "Média (0-20)", key: "averageScore" },
  { header: "Percentagem Média", key: "averagePercentage" },
  { header: " ", key: "actions" },
];

const getCellContent = (cell) => {
  if (cell.info.header === "actions") {
    return (
      <Link to={`../plano-de-negocio/${cell.value}`} title="Consultar">
        <DataView24 />
      </Link>
    );
  }

  return cell.value;
};

const BusinessPlanRankingPage = () => {
  const [rows, setRows] = useState([]);
  const [rankingData, setRankingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submissionWindow, setSubmissionWindow] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const formatScore = (value) =>
    value !== null && value !== undefined ? value.toFixed(2) : "N/A";

  const formatPercentage = (value) =>
    value !== null && value !== undefined ? `${value.toFixed(2)}%` : "N/A";

  const calculateDifferencePercentage = (jury1, jury2) => {
    if (jury1 === null || jury1 === undefined || jury2 === null || jury2 === undefined) {
      return null;
    }
    return (Math.abs(jury1 - jury2) / 20) * 100;
  };

  const exportRankingXLS = () => {
    if (!rankingData.length) {
      toast.warn("Sem dados para exportar");
      return;
    }

    const date = new Date().toISOString().split("T")[0];
    const data = rankingData.map((item) => ({
      "Posicao": item.position,
      "ID Plano": item.businessPlanId,
      "UUID": item.uuid,
      NUR: item.nur,
      Janela: item.windowDesc,
      "# de Avaliacoes": item.totalEvaluations,
      "Juri 1": item.totalScoreJury1 ?? "",
      "Juri 2": item.totalScoreJury2 ?? "",
      "Juri 3": item.totalScoreJury3 ?? "",
      "Diferenca (%)":
        calculateDifferencePercentage(item.totalScoreJury1, item.totalScoreJury2) ??
        "",
      "Media (0-20)": item.averageScore ?? "",
      "Percentagem Media": item.averagePercentage ?? "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ranking");
    XLSX.writeFile(wb, `Ranking_BusinessPlan_${date}.xlsx`);
  };

  const fetchRanking = async (window = submissionWindow) => {
    try {
      setLoading(true);
      const params = window ? `?submissionWindow=${window}` : "";
      const response = await http.get(`/business-plan/ranking${params}`);
      /** @type {BusinessPlanRankingItem[]} */
      const sortedRanking = [...response.data].sort(
        (a, b) => a.position - b.position
      );
      setRankingData(sortedRanking);

      const data = sortedRanking.map((item) => {
        const differencePercentage = calculateDifferencePercentage(
          item.totalScoreJury1,
          item.totalScoreJury2
        );
        const formattedDifference =
          differencePercentage !== null
            ? `${differencePercentage.toFixed(2)}${
                differencePercentage > 30 ? "% (Desempate)" : "%"
              }`
            : "N/A";

        return {
        id: String(item.businessPlanId),
        position: item.position,
        businessPlanId: item.businessPlanId,
        uuid: item.uuid,
        nur: item.nur,
        window: item.windowDesc,
        competitorName: item.competitorName,
        totalEvaluations: item.totalEvaluations,
        totalScoreJury1: formatScore(item.totalScoreJury1),
        totalScoreJury2: formatScore(item.totalScoreJury2),
        totalScoreJury3: formatScore(item.totalScoreJury3),
        differencePercentage: formattedDifference,
        averageScore: formatScore(item.averageScore),
        averagePercentage: formatPercentage(item.averagePercentage),
        actions: item.uuid,
      };
      });
      setRows(data);
      setPage(1);
    } catch (error) {
      toast.error("Erro ao carregar o ranking de planos de negócio");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRanking("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeWindow = (event) => {
    const window = event.target.value;
    setSubmissionWindow(window);
    fetchRanking(window);
  };

  const onPageChange = ({ page: nextPage, pageSize: nextPageSize }) => {
    setPage(nextPage);
    setPageSize(nextPageSize);
  };

  const visibleRows = rows.slice((page - 1) * pageSize, page * pageSize);

  return (
    <Card title="Ranking de Planos de Negócio">
      <Loading
        active={loading}
        description="A carregar ranking..."
        withOverlay={true}
      />
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: "0.5rem",
          marginBottom: "1rem",
        }}
      >
        <Select
          id="ranking-submission-window"
          labelText="Janela"
          value={submissionWindow}
          onChange={changeWindow}
          size="sm"
          style={{ width: "9rem" }}
        >
          <SelectItem value="" text="Todas as janelas" />
          <SelectItem value="JANELA_1" text="Janela 1" />
          <SelectItem value="JANELA_2" text="Janela 2" />
        </Select>
        <Button
          kind="primary"
          size="sm"
          onClick={exportRankingXLS}
          disabled={loading || !rows.length}
        >
          Download do Ranking
        </Button>
      </div>
      <DataTable rows={visibleRows} headers={tableHeaders} useZebraStyles>
        {({ rows: tableRows, headers, getHeaderProps, getTableProps }) => (
          <TableContainer>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableHeader key={header.key} {...getHeaderProps({ header })}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map((row) => (
                  <TableRow key={row.id}>
                    {row.cells.map((cell) => (
                      <TableCell key={cell.id}>{getCellContent(cell)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>
      <TablePagination
        key={submissionWindow}
        currentPageSize={pageSize}
        onPageChange={onPageChange}
        totalItems={rows.length}
      />
    </Card>
  );
};

export default BusinessPlanRankingPage;
